import { createContext, use, type ReactNode } from 'react';
import { api } from '@/lib/api/endpoints';
import type { Principal, StaffRole } from '@/lib/api/types';
import { useAsync, type AsyncState } from '@/lib/hooks/useAsync';

interface IdentityValue {
  state: AsyncState<Principal | null>;
  reload: () => void;
}

const IdentityContext = createContext<IdentityValue | null>(null);

/**
 * Resolves the caller's principal once, from the server.
 *
 * Nothing here is a security control. Cloudflare Access issues the session and
 * the Worker re-validates it on every single request (BR-AP1 / NFR-AP2); this
 * context exists only so the UI can hide navigation a user cannot use. A user
 * who forges a role in the browser sees admin chrome and gets 403s behind it.
 */
export function IdentityProvider({ children }: { children: ReactNode }) {
  const { state, reload } = useAsync<Principal | null>(async () => {
    const { principal } = await api.me();
    return principal;
  }, []);

  return <IdentityContext value={{ state, reload }}>{children}</IdentityContext>;
}

export function useIdentity(): IdentityValue {
  const value = use(IdentityContext);
  if (!value) throw new Error('useIdentity must be used inside <IdentityProvider>');
  return value;
}

/** The resolved principal, or null while loading or when unauthenticated. */
export function usePrincipal(): Principal | null {
  const { state } = useIdentity();
  return state.status === 'ready' ? state.data : null;
}

export function hasRole(principal: Principal | null, ...allowed: StaffRole[]): boolean {
  return principal?.kind === 'staff' && principal.roles.some((role) => allowed.includes(role));
}

export function isAdmin(principal: Principal | null): boolean {
  return hasRole(principal, 'workshop-admin', 'platform-owner');
}

export function isPresenter(principal: Principal | null): boolean {
  return hasRole(principal, 'presenter', 'workshop-admin', 'platform-owner');
}
